import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _has from 'lodash/has';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import {
  makeSelectClientLanding,
  makeSelectClientLandingProcessing,
} from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectClientDetailsFetching,
} from 'containers/Main/selectors';
import { useIsOnboardingEnabled } from 'components/Personalizations/hooks/useIsOnboardingEnabled';
import { showOnboardingQuestions } from 'containers/Main/actions';
import AppContainer from 'components/AppContainer';
import ContentBlock from 'components/ContentBlock';
import { CONTENT_BLOCK_POSITIONS } from 'components/ContentBlock/utils';
import PageIntroBlock from 'components/PageIntroBlock';
import HeroLanding from 'components/HeroLanding';
import PageMeta from 'components/PageMeta';
import VisibleOnScrollTracking from 'components/VisibleOnScrollTracking';
import loadable from 'utils/loadable';
import MixPanel from 'utils/mixpanelService';
import PersonalizedLanding from 'components/Personalizations/PersonalizedLanding';
import HomeIntro from 'components/HomeIntro';
import useClientExcludedResourceType from 'components/Hooks/useClientExcludedResourceType';
import SignedInBanner from './components/SignedInBanner';
import TopicsList from './components/TopicsList';
import Section from './Section';
import { getSavedResources } from '../Resources/actions';
import HomeReferral from '../../components/HomeReferral';
import { isBareEmbedded } from '../../utils/embedded';
import ClientFontWrapper from '../../components/ClientFontWrapper';

const Disclaimer = loadable(
  () => import('components/ResourceAndAssessmentDisclaimer'),
  {
    fallback: null,
  },
);
const ClientFactoids = loadable(() => import('./components/ClientFactoids'), {
  fallback: null,
});

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientDetailsFetching: makeSelectClientDetailsFetching(),
  auth: makeSelectAuth(),
  clientLandingInfo: makeSelectClientLanding(),
  clientLandingFetching: makeSelectClientLandingProcessing(),
  profile: makeSelectProfile(),
});

function HomePage() {
  const {
    auth,
    clientDetails,
    clientDetailsFetching,
    clientLandingInfo,
    clientLandingFetching,
    profile,
  } = useSelector(stateSelector);
  const dispatch = useDispatch();
  const isOnboardingEnabled = useIsOnboardingEnabled();
  const { clientExcludedResourceTypes } = useClientExcludedResourceType();
  const showAsLanding = !_isEmpty(
    _get(clientDetails, 'landingLinksCollection.items', []),
  );

  const history = useHistory();

  const profileOnboarding = _get(profile, 'onboarding');
  const showPersonalizedLanding =
    isOnboardingEnabled && !_isEmpty(_get(profileOnboarding, 'topics', []));
  const isHomePageLoaded =
    _has(auth, 'isLoaded') &&
    auth.isLoaded &&
    !_isEmpty(clientLandingInfo) &&
    !clientLandingFetching;

  const getOnboardingPath = () => {
    const queryparams = new URLSearchParams(window.location.search);
    const onboardingPath = queryparams.get('onboarding_path');
    return onboardingPath || 'onboarding';
  };

  useEffect(() => {
    const isOnboardingEmpty =
      !profile.isEmpty && _isEmpty(_get(profile, 'onboarding', null));
    const isOnboardingCompleted = !!_get(profile, 'onboarding.topics');
    const isOnboardingPath = history.location.pathname === '/onboarding/start';
    if (
      isOnboardingEnabled &&
      ((isOnboardingPath && !isOnboardingCompleted) || isOnboardingEmpty)
    ) {
      dispatch(showOnboardingQuestions(true));
      const onboardingPath = getOnboardingPath();
      MixPanel.track('Onboarding - Viewed', {
        path: onboardingPath,
      });
    }
  }, [isOnboardingEnabled, dispatch, profile, history.location.pathname]);

  useEffect(() => {
    // wait for auth and landing info is loaded so we can identify which variant
    if (!isHomePageLoaded) return;
    MixPanel.track(`Homepage - Loaded and Viewed`);
    dispatch(getSavedResources());
  }, [isHomePageLoaded, dispatch]);

  if (
    clientDetailsFetching ||
    (showAsLanding && (_isEmpty(clientLandingInfo) || clientLandingFetching))
  )
    return <Skeleton variant="rect" height="500px" />;

  const shouldShowClientResourcesList = _get(
    clientLandingInfo,
    'pageText.clientResourcesDisplay',
    true,
  );
  const defaultSections = [
    'Banner',
    'Topics',
    'Resources',
    'Lists',
    'Blogs',
    'News',
    'Subscribe',
  ];
  const landingSections = _get(clientLandingInfo, 'optionalSections');
  const finalSections = !_isEmpty(landingSections)
    ? landingSections
    : defaultSections;
  let pageMeta = '';
  let pageType = 'homepage';
  let canonicalUrl = `${window.location.origin}/`;
  if (showAsLanding && _get(clientDetails, 'shortName')) {
    pageType = 'landing';
    pageMeta = clientLandingInfo;
    canonicalUrl = `https://${clientDetails.shortName}.crediblemind.com/`;
  }
  const hasClientSection = finalSections.includes('Client');
  const hasBanner = finalSections.includes('Banner');
  const showReferral = _get(clientDetails, 'metadata.showReferrals');

  return (
    <ClientFontWrapper>
      <div id="homepage">
        <PageMeta data={pageMeta} type={pageType} canonicalUrl={canonicalUrl} />
        {!isBareEmbedded() && <HeroLanding />}

        <ContentBlock
          type="common-page"
          rounded={false}
          marginTop={false}
          position={CONTENT_BLOCK_POSITIONS.BELOW_HERO_INTRO}
        />
        <HomeIntro />
        {showReferral && <HomeReferral />}
        <ContentBlock
          type="common-page"
          position={CONTENT_BLOCK_POSITIONS.USE_LANDING_SECTIONS}
          index={0}
        />
        {hasBanner && (
          <SignedInBanner
            grayscale={_get(clientLandingInfo, 'pageText.grayscale', false)}
            bgColor={_get(clientLandingInfo, 'pageText.landingBannerBGColor')}
          />
        )}
        {hasBanner && (
          <Section
            section="Banner"
            grayscale={_get(clientLandingInfo, 'pageText.grayscale', false)}
            bgColor={_get(clientLandingInfo, 'pageText.landingBannerBGColor')}
          />
        )}
        <ContentBlock
          type="common-page"
          position={CONTENT_BLOCK_POSITIONS.USE_LANDING_SECTIONS}
          index={1}
        />
        {showAsLanding && !isBareEmbedded() && (
          <AppContainer customPadding>
            <PageIntroBlock
              type="reference"
              slug={clientLandingInfo.slug}
              page="landing"
            />
          </AppContainer>
        )}
        {!hasClientSection && shouldShowClientResourcesList && (
          <ClientFactoids
            mt50={!hasClientSection}
            grayscale={_get(clientLandingInfo, 'pageText.grayscale', false)}
          />
        )}
        <ContentBlock
          type="common-page"
          position={CONTENT_BLOCK_POSITIONS.USE_LANDING_SECTIONS}
          index={2}
        />
        {showPersonalizedLanding && (
          <PersonalizedLanding
            profile={profile}
            grayscale={_get(clientLandingInfo, 'pageText.grayscale', false)}
          />
        )}
        {finalSections.map((section, index) => {
          if (section === 'Banner') return null;
          if (clientExcludedResourceTypes.includes(section)) return null;
          if (
            section === 'Blogs' &&
            clientExcludedResourceTypes.includes('Insights')
          )
            return null;
          if (section === 'Topics') {
            return (
              <React.Fragment key={section}>
                <ContentBlock
                  type="common-page"
                  position={CONTENT_BLOCK_POSITIONS.USE_LANDING_SECTIONS}
                  index={3 + index}
                />
                <VisibleOnScrollTracking section="topics">
                  <TopicsList
                    topicsList={
                      showAsLanding
                        ? {
                            data: _get(
                              clientLandingInfo,
                              'topTopicsCollection.items',
                            ),
                          }
                        : null
                    }
                    grayscale={_get(
                      clientLandingInfo,
                      'pageText.grayscale',
                      false,
                    )}
                  />
                </VisibleOnScrollTracking>
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={section}>
              <ContentBlock
                type="common-page"
                position={CONTENT_BLOCK_POSITIONS.USE_LANDING_SECTIONS}
                index={3 + index}
              />
              <Section
                section={section}
                grayscale={_get(clientLandingInfo, 'pageText.grayscale', false)}
                useClientImage={_get(
                  clientLandingInfo,
                  'pageText.useClientImage',
                  false,
                )}
              />
            </React.Fragment>
          );
        })}
        <LazyLoadComponent>
          <Disclaimer type="resources-disclaimer" />
        </LazyLoadComponent>
      </div>
    </ClientFontWrapper>
  );
}

export default HomePage;
